import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './schedules.module.scss';
import { SchedulesProps } from '../../../interfaces/components';
import { useToast } from "../../../helpers/hooks";
import ProfileCard from "../../../components/ProfileCard";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from "@fullcalendar/timegrid";
import allLocales from '@fullcalendar/core/locales-all';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import { StyleWrapper } from '../../../constants/Schedules';
import { capitalize } from '../../../helpers/app';

const Schedules = ({
    schedules,
    getSchedules,
    cleanSchedules
}: SchedulesProps) => {
    const toast = useToast();
    const calendar = useRef<InstanceType<typeof FullCalendar>>(null);
    const [buttonSelected, setButtonSelected] = useState<any>();
    const [date, setDate] = useState<any>({
        view: "",
        startDate: "",
        endDate: ""
    });

    const setRangeDate = (calendarApi: any) => {
        let view = "month";
        switch(calendarApi.currentData.currentViewType){
            case "timeGridWeek":
                view = "week";
                break;
            case "timeGridDay":
                view = "day";
                break;
            default:
                view = "month";
                break;
        }
        const range = calendarApi.currentData.dateProfile.currentRange;
        let startDate = moment(range.start).add(1, "d").set({hour: moment("00:00:00", "HH:mm").get("hour"), minute: moment("00:00:00", "HH:mm").get("minute")}).format("YYYY-MM-DD HH:mm");
        let endDate = moment(range.end).set({hour: moment("23:59:59", "HH:mm").get("hour"), minute: moment("23:59:59", "HH:mm").get("minute")}).format("YYYY-MM-DD HH:mm");
        setDate({view: view, startDate: startDate, endDate: endDate});
    }

    const handleClickToday = (calendarApi: any) => {
        calendarApi.today();
        setRangeDate(calendarApi);
    }

    const handleClickNext = (calendarApi: any) => {
        calendarApi.next();
        setRangeDate(calendarApi);
    }

    const handleClickPrev = (calendarApi: any) => {
        calendarApi.prev();
        setRangeDate(calendarApi);
    }

    const handleChangeView = (calendarApi: any, e: any, view: string) => {
        if(buttonSelected && buttonSelected.target === e?.target) return;
        if(buttonSelected && buttonSelected.target !== e?.target){
            buttonSelected.target.className = buttonSelected.target.className.replace("timeSelected", "");
        }
        e.target.className = `${e?.target?.className} timeSelected`;
        setButtonSelected(e);
        calendarApi.changeView(view);
        setRangeDate(calendarApi);
    }

    useEffect(() => {
        if(date.view){
            if(date.view === "day"){
                getSchedules(date.startDate, date.endDate);
            }else{
                getSchedules(moment(date.startDate).format("YYYY-MM-DD"), moment(date.endDate).format("YYYY-MM-DD"));
            }
        }
    }, [date]);

    const events: any = useMemo(() => {
        const res = schedules.data?.inspection_schedules && schedules.data?.inspection_schedules.map((item: any) => {
            const participant = item?.participants.find((participant: any) => participant?.role === "seller");
            return {
                title: (item?.pub_brief?.title + " " +  item?.pub_brief?.year), 
                start: item?.date_start, 
                end: item?.date_end,
                allDay: (item?.date_start === item?.date_end),
                seller: `${capitalize(participant?.name)} ${capitalize(participant?.last_name)}`,
            }
        })
        return res;
    }, [schedules.data]);

    useEffect(() => {
        if(schedules?.fetching === "failure"){
            toast("Ha ocurrido un error al traer los agendamientos");
            cleanSchedules();
        }
        if(schedules?.fetching === "success"){
            cleanSchedules();
        }
    }, [schedules?.fetching]);

    useEffect(() => {
        getSchedules(moment().startOf("M").format("YYYY-MM-DD"), moment().endOf("M").format("YYYY-MM-DD"));
    }, []);

    return (
        <ProfileCard>
            <div className={styles.schedules}>
                <StyleWrapper>
                    <FullCalendar
                        ref={calendar}
                        events={events}
                        eventColor='#1A1C21'
                        eventClassNames={styles.event}
                        eventContent={function(e: any) {
                            let elem = document.createElement('p');
                            const data = `
                                <span>${(e.event.allDay ? "Todo el dia" : moment(e.event.start).format("HH:mm").toString())}</span>
                                <span>${e.event.title} - <strong>${e.event._def.extendedProps.seller}</strong></span>
                            `;
                            elem.innerHTML = data;
                            let arrayOfDomNodes = [ elem ]
                            return { domNodes: arrayOfDomNodes }
                        }}
                        plugins={[ dayGridPlugin, timeGridPlugin, interactionPlugin ]}
                        initialView="dayGridMonth"
                        height="auto"
                        locales={allLocales}
                        locale="es"
                        slotMinTime="08:00:00"
                        slotMaxTime="19:00:00"
                        slotLabelFormat={{
                            hour: 'numeric',
                            minute: '2-digit',
                            meridiem: 'short'
                        }}
                        customButtons={{
                            prevButton: {
                                click: () => handleClickPrev(calendar.current?.getApi()),
                            },
                            nextButton: {
                                click: () => handleClickNext(calendar.current?.getApi()),
                            },
                            todayButton: {
                                text: 'Hoy',
                                click: () => handleClickToday(calendar.current?.getApi()),
                            },
                            toggleMonth: {
                                text: 'Mes',
                                click: (e: any) => handleChangeView(calendar.current?.getApi(), e, "dayGridMonth"),
                            },
                            toggleWeek: {
                                text: 'Sem',
                                click: (e: any) => handleChangeView(calendar.current?.getApi(), e, "timeGridWeek"),
                            },
                            toggleDay: {
                                text: 'Día',
                                click: (e: any) => handleChangeView(calendar.current?.getApi(), e, "timeGridDay"),
                            }
                        }}
                        buttonIcons={{
                            prevButton: 'chevron-left',
                            nextButton: 'chevron-right',
                        }}
                        views={{
                            dayGridMonth: {
                                titleFormat: {year: 'numeric', month: 'long'}
                            },
                        }}
                        headerToolbar={{
                            start: 'prevButton,nextButton todayButton',
                            center: 'title',
                            end: 'toggleMonth,toggleWeek,toggleDay',
                        }}
                    />
                </StyleWrapper>
            </div>
        </ProfileCard>
    )
};
export default Schedules;